import { useEffect, useState } from "react";

import { Encounter, Practitioner, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Group } from "@mantine/core";

import { format } from "date-fns";

import { TextWrapper } from "@/components/TextWrapper";
import { useEncounterSignedAt } from "@/encounters/hooks/useEncounterSignedAt";
import { resolveEncounterProviderDisplay } from "@/encounters/services/encounter-provider-display";
import { fetchEncounterQuestionnaire } from "@/encounters/services/questionnaire";
import { fetchEncounterQuestionnaireResponse } from "@/encounters/services/questionnaire-response";
import { Status } from "@/encounters/zod-schemas/encounters";
import { getPractitionerNameWithCredentials } from "@/utils/person";

interface EncounterSignatureProps {
  encounter: Encounter;
  className?: string;
}

export function EncounterSignature({ encounter, className }: EncounterSignatureProps) {
  const medplum = useMedplum();
  const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse | null>(
    null,
  );
  const [practitioner, setPractitioner] = useState<Practitioner | null>(null);
  /** `undefined` = not loaded yet for finished encounters */
  const [finishedSignerLabel, setFinishedSignerLabel] = useState<string | undefined>(undefined);
  const { signedAt: finishedSignedAt, isLoading: isFinishedSignedAtLoading } =
    useEncounterSignedAt(encounter);

  useEffect(() => {
    let cancelled = false;

    const resolveFinishedSignerLabel = async () => {
      if (encounter.status === Status.Finished) {
        setQuestionnaireResponse(null);
        setPractitioner(null);
        setFinishedSignerLabel(undefined);
        try {
          const name = await resolveEncounterProviderDisplay(medplum, encounter);
          if (!cancelled) setFinishedSignerLabel(name?.trim() ?? "");
        } catch (error) {
          console.error("Error resolving finished encounter signer:", error);
          if (!cancelled) setFinishedSignerLabel("");
        }
        return;
      }

      setFinishedSignerLabel(undefined);

      try {
        const questionnaire = await fetchEncounterQuestionnaire(medplum, encounter);
        if (!questionnaire) return;

        const patientId = encounter.subject?.reference?.split("/")?.[1];
        if (!patientId) return;

        const response = await fetchEncounterQuestionnaireResponse({
          encounterId: encounter.id!,
          questionnaireId: questionnaire.id!,
          clientId: patientId,
          medplum,
        });

        if (cancelled) return;
        setQuestionnaireResponse(response);

        if (response?.author?.reference) {
          const practitionerId = response.author.reference.split("/")[1];
          const practitionerResource = await medplum.readResource("Practitioner", practitionerId);
          if (!cancelled) setPractitioner(practitionerResource);
        } else if (!cancelled) {
          setPractitioner(null);
        }
      } catch (error) {
        console.error("Error fetching questionnaire response:", error);
      }
    };

    void resolveFinishedSignerLabel();
    return () => {
      cancelled = true;
    };
  }, [encounter, medplum]);

  if (encounter.status === Status.Finished) {
    if (finishedSignerLabel === undefined || isFinishedSignedAtLoading) return null;

    return (
      <Group justify="flex-end" className={className}>
        <TextWrapper fz="md" c="dark.9">
          Signed by {finishedSignerLabel}
          {finishedSignedAt ? ` - ${format(new Date(finishedSignedAt), "MM/dd/yyyy")}` : ""}
        </TextWrapper>
      </Group>
    );
  }

  const signedAt = questionnaireResponse?.meta?.lastUpdated;
  if (!signedAt) return null;

  const practitionerName = getPractitionerNameWithCredentials(
    practitioner || undefined,
    questionnaireResponse?.meta?.author?.display,
  );

  return (
    <Group justify="flex-end" className={className}>
      <TextWrapper fz="md" c="dark.9">
        Signed by {practitionerName} - {format(new Date(signedAt), "MM/dd/yyyy")}
      </TextWrapper>
    </Group>
  );
}
